import React, { useState } from 'react';
import { KeyIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import SnowflakeConnectionForm from './SnowflakeConnectionForm';
import SnowflakeKeyPairForm from './SnowflakeKeyPairForm';

const EnhancedSnowflakeForm = ({ 
  details, 
  onChange, 
  errors = {}, 
  onOAuthSuccess 
}) => {
  const [connectionMethod, setConnectionMethod] = useState('keypair');

  const handleMethodChange = (method) => {
    setConnectionMethod(method);
    
    // Clear any existing details when switching methods
    onChange({});
  };

  const handleOAuthSuccess = (result) => {
    if (typeof onOAuthSuccess === 'function') {
      onOAuthSuccess(result);
    }
  };

  return (
    <div className="space-y-6">
      {/* Connection Method Selector */}
      <div className="border-b border-secondary-200 pb-6">
        <h3 className="text-lg font-medium text-secondary-900 mb-4">
          Choose Authentication Method
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Key Pair Option - Recommended */}
          <button
            type="button"
            onClick={() => handleMethodChange('keypair')}
            className={`relative rounded-lg border-2 p-4 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              connectionMethod === 'keypair'
                ? 'border-primary-500 bg-primary-50'
                : 'border-secondary-300 bg-white hover:border-secondary-400'
            }`}
          >
            <div className="flex items-start space-x-3">
              <CpuChipIcon className={`h-6 w-6 mt-1 ${
                connectionMethod === 'keypair' ? 'text-primary-600' : 'text-secondary-400'
              }`} />
              <div className="flex-1 text-left">
                <span className={`text-sm font-medium ${
                  connectionMethod === 'keypair' ? 'text-primary-900' : 'text-secondary-900'
                }`}>
                  Key Pair (Recommended)
                </span>
                <p className={`mt-1 text-xs ${
                  connectionMethod === 'keypair' ? 'text-primary-700' : 'text-secondary-500'
                }`}>
                  Secure service account authentication using public/private key pairs.
                </p>
              </div>
            </div>
            
            {connectionMethod === 'keypair' && (
              <div className="absolute -top-2 -right-2">
                <div className="bg-primary-500 rounded-full p-1">
                  <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </button>

          {/* Credentials Option */}
          <button
            type="button"
            onClick={() => handleMethodChange('credentials')}
            className={`relative rounded-lg border-2 p-4 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              connectionMethod === 'credentials'
                ? 'border-primary-500 bg-primary-50'
                : 'border-secondary-300 bg-white hover:border-secondary-400'
            }`}
          >
            <div className="flex items-start space-x-3">
              <KeyIcon className={`h-6 w-6 mt-1 ${
                connectionMethod === 'credentials' ? 'text-primary-600' : 'text-secondary-400'
              }`} />
              <div className="flex-1 text-left">
                <span className={`text-sm font-medium ${
                  connectionMethod === 'credentials' ? 'text-primary-900' : 'text-secondary-900'
                }`}>
                  Username & Password
                </span>
                <p className={`mt-1 text-xs ${
                  connectionMethod === 'credentials' ? 'text-primary-700' : 'text-secondary-500'
                }`}>
                  Traditional authentication using your Snowflake username and password.
                </p>
              </div>
            </div>
            
            {connectionMethod === 'credentials' && (
              <div className="absolute -top-2 -right-2">
                <div className="bg-primary-500 rounded-full p-1">
                  <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Render appropriate form based on selected method */}
      <div>
        {connectionMethod === 'keypair' ? (
          <SnowflakeKeyPairForm
            onConnectionCreated={handleOAuthSuccess}
            onCancel={() => handleMethodChange('credentials')}
          />
        ) : (
          <div>
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Enter Your Snowflake Credentials
            </h3>
            <SnowflakeConnectionForm
              details={details}
              onChange={onChange}
              errors={errors}
            />
          </div>
        )}
      </div>

      {/* Security Note for Credentials */}
      {connectionMethod === 'credentials' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-amber-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Security Notice
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                Your credentials are encrypted and stored securely. For enhanced security, 
                consider using key pair authentication instead.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedSnowflakeForm;